import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Block as BlockIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import SearchableSelect from '../../components/SearchableSelect';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import integrationAccessTokenService, {
  decodeJwtPayloadUnsafe,
  IntegrationAccessTokenCreated,
  IntegrationAccessTokenListItem,
  ValidateIntegrationTokenResult,
} from '../../services/integrationAccessTokenService';

const IntegrationAccessTokens: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, tenantId, tenants } = useAuth();
  const [rows, setRows] = useState<IntegrationAccessTokenListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterTenantId, setFilterTenantId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState('');
  const [expiresLocal, setExpiresLocal] = useState('');
  const [createTenantId, setCreateTenantId] = useState<number | ''>('');
  const [created, setCreated] = useState<IntegrationAccessTokenCreated | null>(null);
  const [validateOpen, setValidateOpen] = useState(false);
  const [validateInput, setValidateInput] = useState('');
  const [validateLoading, setValidateLoading] = useState(false);
  const [validateResult, setValidateResult] = useState<ValidateIntegrationTokenResult | null>(null);

  const validateJwtPreview = useMemo(() => decodeJwtPayloadUnsafe(validateInput), [validateInput]);
  const validateTokenUse = validateJwtPreview ? String(validateJwtPreview.token_use ?? '') : '';

  const isSuperAdmin = user?.roles?.includes('SUPERADMIN');
  const tenantOptions = useMemo(() => {
    if (tenants && tenants.length > 0) return tenants;
    if (tenantId != null) {
      return [{ id: tenantId, code: '', name: 'Tenant actual' }];
    }
    return [];
  }, [tenants, tenantId]);

  const tenantSelectOptions = useMemo(
    () =>
      tenantOptions.map((t) => ({
        value: t.id,
        label: `${t.code ? `${t.code} — ` : ''}${t.name} (${t.id})`,
      })),
    [tenantOptions],
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const tid = filterTenantId ?? undefined;
      const data = await integrationAccessTokenService.list(tid);
      setRows(data);
    } catch (e: unknown) {
      console.error(e);
      enqueueSnackbar('Error al cargar tokens de integración', { variant: 'error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, filterTenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (createOpen && createTenantId === '' && tenantOptions.length > 0) {
      const def = tenantId != null && tenantOptions.some((t) => t.id === tenantId) ? tenantId : tenantOptions[0].id;
      setCreateTenantId(def);
    }
  }, [createOpen, createTenantId, tenantId, tenantOptions]);

  const handleOpenCreate = () => {
    if (!tenantOptions.length) {
      enqueueSnackbar('No hay tenant en la sesión. Vuelva a iniciar sesión o cambie de empresa.', { variant: 'warning' });
      return;
    }
    setName('');
    setScopes('');
    setExpiresLocal('');
    setCreateTenantId(tenantId != null && tenantOptions.some((t) => t.id === tenantId) ? tenantId : tenantOptions[0].id);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      enqueueSnackbar('Ingrese un nombre', { variant: 'warning' });
      return;
    }
    if (createTenantId === '') {
      enqueueSnackbar('Seleccione un tenant', { variant: 'warning' });
      return;
    }
    if (!expiresLocal) {
      enqueueSnackbar('Seleccione fecha y hora de expiración', { variant: 'warning' });
      return;
    }
    const exp = new Date(expiresLocal);
    if (Number.isNaN(exp.getTime())) {
      enqueueSnackbar('Fecha de expiración inválida', { variant: 'warning' });
      return;
    }
    const now = new Date();
    const yearsAhead = (exp.getTime() - now.getTime()) / (365.25 * 24 * 3600 * 1000);
    if (yearsAhead > 2) {
      if (!window.confirm('La expiración es en más de 2 años. ¿Confirma?')) return;
    }
    try {
      const body = {
        tenantId: createTenantId as number,
        expiresAt: exp.toISOString(),
        name: name.trim(),
        scopes: scopes.trim() || undefined,
      };
      const res = await integrationAccessTokenService.create(body);
      setCreateOpen(false);
      setCreated(res);
      enqueueSnackbar('Token creado', { variant: 'success' });
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Error al crear token';
      enqueueSnackbar(String(msg), { variant: 'error' });
    }
  };

  const handleRevoke = async (id: number) => {
    if (!window.confirm('¿Revocar este token? Dejará de funcionar de inmediato.')) return;
    try {
      await integrationAccessTokenService.revoke(id);
      enqueueSnackbar('Token revocado', { variant: 'success' });
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Error al revocar';
      enqueueSnackbar(String(msg), { variant: 'error' });
    }
  };

  const copyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      enqueueSnackbar('Copiado al portapapeles', { variant: 'success' });
    } catch {
      enqueueSnackbar('No se pudo copiar', { variant: 'error' });
    }
  };

  const handleOpenValidate = () => {
    setValidateInput('');
    setValidateResult(null);
    setValidateOpen(true);
  };

  const handleValidatePing = async () => {
    if (!validateInput.trim()) {
      enqueueSnackbar('Pegue el token (JWT) o el encabezado Bearer completo', { variant: 'warning' });
      return;
    }
    setValidateLoading(true);
    setValidateResult(null);
    try {
      const result = await integrationAccessTokenService.validateWithPing(validateInput);
      setValidateResult(result);
      if (result.success) {
        enqueueSnackbar('Token válido: la API respondió correctamente al ping', { variant: 'success' });
      } else if (result.status === 403) {
        enqueueSnackbar('403: ese JWT no es de integración (p. ej. token de login de usuario). Ver detalle.', { variant: 'warning' });
      } else {
        enqueueSnackbar('El token no pasó la validación (ver detalle abajo)', { variant: 'error' });
      }
    } catch (e: unknown) {
      console.error(e);
      const msg = e instanceof Error ? e.message : 'Error de red al llamar a la API';
      setValidateResult({ success: false, status: 0, detail: msg });
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setValidateLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Tokens de integración
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" color="primary" startIcon={<FactCheckIcon />} onClick={handleOpenValidate}>
                Validar token
              </Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                Nuevo token
              </Button>
            </Stack>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fechas en pantalla son locales; al guardar se envían a la API en ISO (UTC). El valor del Bearer solo se muestra una vez al crearlo. &quot;Validar token&quot; llama a{' '}
            <Box component="span" sx={{ fontFamily: 'monospace' }}>GET /api/integration/ping</Box> con la misma apiUrl que usa esta app (p. ej. config.json).
          </Typography>

          {isSuperAdmin && (
            <SearchableSelect<number>
              label="Filtrar por tenant"
              value={filterTenantId}
              onChange={setFilterTenantId}
              options={tenantSelectOptions}
              allowEmpty
              emptyOptionLabel="Todos"
              size="small"
              sx={{ minWidth: 280, mb: 2 }}
            />
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Creado</TableCell>
                  <TableCell>Expira</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>
                      {r.tenantCode || '-'} ({r.tenantId})
                    </TableCell>
                    <TableCell>{new Date(r.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{new Date(r.expiresAt).toLocaleString()}</TableCell>
                    <TableCell>{r.isRevoked ? 'Revocado' : 'Vigente'}</TableCell>
                    <TableCell align="right">
                      {!r.isRevoked && (
                        <Tooltip title="Revocar">
                          <IconButton size="small" color="warning" onClick={() => void handleRevoke(r.id)}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary">{loading ? 'Cargando…' : 'Sin registros'}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={validateOpen}
        onClose={() => {
          if (!validateLoading) setValidateOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Validar token de integración</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" variant="outlined">
              Solo sirve el JWT largo que se muestra <strong>una vez</strong> al crear un token aquí. El token del inicio de sesión (usuario y contraseña) es otro tipo de JWT: la API responde <strong>403</strong> en este ping porque no incluye el claim{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>token_use=integration</Box>.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Se enviará solo a la API configurada para el front (mismo origen que crear/listar tokens). Puede pegar el JWT o la línea completa{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>Bearer …</Box>.
            </Typography>
            {validateJwtPreview && validateTokenUse !== 'integration' && (
              <Alert severity="warning">
                El texto pegado no declara <Box component="span" sx={{ fontFamily: 'monospace' }}>token_use=integration</Box>
                {validateTokenUse ? ` (valor actual: ${validateTokenUse})` : ' (sin claim token_use)'}.
                Si es el token de la sesión web, el ping devolverá 403 aunque el usuario pueda usar el sistema con normalidad.
              </Alert>
            )}
            {validateJwtPreview && validateTokenUse === 'integration' && (
              <Alert severity="success" variant="outlined">
                El payload incluye <Box component="span" sx={{ fontFamily: 'monospace' }}>token_use=integration</Box>. Si aun así falla, revise 401 en el detalle (misma API, firma, iss/aud).
              </Alert>
            )}
            <TextField
              label="Token (JWT o Bearer …)"
              value={validateInput}
              onChange={(e) => setValidateInput(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9…"
              disabled={validateLoading}
            />
            {validateResult && validateResult.success && (
              <Alert severity="success">
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Respuesta del ping
                </Typography>
                <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {JSON.stringify(validateResult.data, null, 2)}
                </Box>
              </Alert>
            )}
            {validateResult && !validateResult.success && (
              <Alert severity="error">
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  No autorizado o error ({validateResult.status || '—'})
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {validateResult.detail}
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidateOpen(false)} disabled={validateLoading}>
            Cerrar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleValidatePing()}
            disabled={validateLoading}
            startIcon={validateLoading ? <CircularProgress size={18} color="inherit" /> : <FactCheckIcon />}
          >
            Probar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo token de integración</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
            <TextField label="Scopes (opcional)" value={scopes} onChange={(e) => setScopes(e.target.value)} fullWidth />
            {tenantOptions.length > 1 ? (
              <SearchableSelect<number>
                label="Tenant"
                value={createTenantId === '' ? null : createTenantId}
                onChange={(val) => setCreateTenantId(val ?? '')}
                options={tenantSelectOptions}
                required
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Tenant: {createTenantId !== '' ? `${createTenantId}` : '—'}
              </Typography>
            )}
            <TextField
              label="Expira (fecha y hora local)"
              type="datetime-local"
              value={expiresLocal}
              onChange={(e) => setExpiresLocal(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => void handleCreate()}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(created)} onClose={() => setCreated(null)} maxWidth="md" fullWidth>
        <DialogTitle>Token generado (guárdelo ahora)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            Este valor no se volverá a mostrar. Copie el Bearer y guárdelo en un lugar seguro.
          </Typography>
          {created && (
            <Stack spacing={2}>
              <TextField label="Bearer (JWT)" value={created.token} fullWidth multiline minRows={3} InputProps={{ readOnly: true }} />
              <Button startIcon={<ContentCopyIcon />} variant="outlined" onClick={() => void copyToken(created.token)}>
                Copiar token
              </Button>
              <Typography variant="caption" display="block">
                JTI: {created.jti} — Expira (UTC): {created.expiresAt}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreated(null)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntegrationAccessTokens;
